/* --- CLEANER, MODERNIZED UI --- */

import { useSelector } from "react-redux";
import AltProfilePic from "../Assets/profileIcon.png";
import { useNavigate, useParams } from "react-router";
import { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import MedicationSummary from "../Components/MedicationSummary";
import Reports from "../Components/Reports";
import HealthGraphs from "../Components/HealthGraphs";
import PrescriptionForm from "../Components/PrescriptionForm";
import Modal from "../Components/Modal";
import useApi from "../customHooks/useAPI";
import AllPrescriptions from "../Components/AllPrescriptions";

const Patient = () => {
    const { id } = useParams();
    const reduxPatients = useSelector((state) => state.patients.patients);
    const { doctorId } = useAuth();
    const navigate = useNavigate();

    const [isOpen, setIsOpen] = useState(false);
    const [patientInfo, setPatientInfo] = useState(null);
    const [note, setNote] = useState("");
    const [saving, setSaving] = useState(false);
    const [medicationData, setMedicationData] = useState(null);
    const saveTimeout = useRef(null);

    const { callApi: callPatients } = useApi();
    const { callApi: callNotes } = useApi();
    const { callApi } = useApi();

    const consultationPageNavigate = () => navigate(`/consultation/${id}`);

    const handleImgError = (e) => {
        e.target.onerror = null;
        e.target.src = AltProfilePic;
    };

    /* ---------- FETCH PATIENTS + NOTES ---------- */
    const fetchPatients = async () => {
        try {
            const [patientsData, notesData] = await Promise.all([
                callPatients({
                    url: `${process.env.REACT_APP_BACKEND_ENDPOINT}/doctor/myPatients`,
                    headers: { "doctor-id": doctorId }
                }),
                callNotes({
                    url: `${process.env.REACT_APP_BACKEND_ENDPOINT}/doctor/allPatientNotes`,
                    headers: { "doctor-id": doctorId }
                })
            ]);

            const merged = patientsData.data.map((p) => {
                const note = notesData.data.find((n) => n.patientId === p.id);
                return { ...p, note: note?.note || "" };
            });

            return merged;
        } catch (error) {
            console.error("Fetch error:", error);
            return [];
        }
    };

    /* ---------- LOAD PATIENT INFO ---------- */
    useEffect(() => {
        const loadPatientData = async () => {
            try {
                let patient =
                    reduxPatients?.find((p) => String(p.id) === String(id)) ||
                    JSON.parse(localStorage.getItem("patientInfo"));

                if (!patient) {
                    const all = await fetchPatients();
                    patient = all.find((p) => String(p.id) === String(id));
                    localStorage.setItem("patientInfo", JSON.stringify(patient));
                }

                if (patient) {
                    setPatientInfo(patient);
                    setNote(patient.note || "");
                }

                const meds = await callApi({
                    url: `${process.env.REACT_APP_BACKEND_ENDPOINT}/doctor/medicationWiseComplianceSummary`,
                    method: "GET",
                    headers: {
                        "Doctor-Id": doctorId,
                        "Patient-Id": id
                    }
                });

                setMedicationData(meds.data);
            } catch (err) {
                console.error("Error loading patient data:", err);
            }
        };

        loadPatientData();

        return () => {
            localStorage.removeItem("patientInfo");
            clearTimeout(saveTimeout.current);
        };
    }, [id]);

    useEffect(() => {
        if (patientInfo) {
            localStorage.setItem("patientInfo", JSON.stringify(patientInfo));
        }
    }, [patientInfo]);

    /* ---------- SAVE NOTES WITH DEBOUNCE ---------- */
    const saveNote = async (newNote) => {
        try {
            setSaving(true);

            const res = await callApi({
                url: `${process.env.REACT_APP_BACKEND_ENDPOINT}/doctor/patientNotes`,
                method: "POST",
                headers: {
                    "doctor-Id": doctorId,
                    "patient-Id": id,
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({ note: newNote })
            });

            if (!res.ok) throw new Error("Failed to save note");

            const updated = { ...patientInfo, note: newNote };
            setPatientInfo(updated);
            localStorage.setItem("patientInfo", JSON.stringify(updated));
        } catch (err) {
            console.error(err);
        } finally {
            setSaving(false);
        }
    };

    const handleNoteChange = (e) => {
        const newNote = e.target.value;
        setNote(newNote);

        clearTimeout(saveTimeout.current);
        saveTimeout.current = setTimeout(() => saveNote(newNote), 1000);
    };

    /* ========= UI BELOW ========= */

    if (!patientInfo) {
        return (
            <div className="p-6 text-center text-gray-700">
                <h2 className="text-xl font-bold">Patient not found</h2>
            </div>
        );
    }

    return (
        <div className="py-20 bg-gray-100">

            {/* ================= TOP SECTION (50vh) ================= */}
            <div className="flex gap-4 px-4 h-[50vh]">

                {/* ---------- LEFT SIDEBAR (reduced width) ---------- */}
                <div className="w-[18%] flex flex-col gap-4">

                    {/* Patient Info Card */}
                    <div className="bg-white p-4 rounded-xl shadow-sm border h-[48%]">
                        <div className="flex items-center gap-3">
                            <img
                                src={patientInfo?.image_url ?? AltProfilePic}
                                alt="Patient"
                                className="w-16 h-16 rounded-full object-cover border"
                                onError={handleImgError}
                            />
                            <div>
                                <h2 className="font-bold text-gray-800 text-base leading-tight">
                                    {patientInfo?.first_name} {patientInfo?.last_name}
                                </h2>
                                <p className="text-xs text-gray-500"><strong>Sex:</strong> {patientInfo?.sex}</p>
                                <p className="text-xs text-gray-500"><strong>Age:</strong> {patientInfo?.age}</p>
                                <p className="text-[10px] text-gray-400">
                                    Created: {new Date(patientInfo?.created_at).toLocaleDateString()}
                                </p>
                            </div>
                        </div>

                        <div className="text-xs text-gray-700 mt-3 space-y-1">
                            <p><strong>Email:</strong> {patientInfo.email || "N/A"}</p>
                            <p><strong>Phone:</strong> {patientInfo.phone_no || "N/A"}</p>
                        </div>

                        <div className="mt-3 flex flex-col gap-1 text-sm">
                            <button
                                onClick={consultationPageNavigate}
                                className="text-blue-600 hover:underline"
                            >
                                Schedule Consultation
                            </button>
                        </div>
                    </div>

                    {/* Notes Card */}
                    <div className="bg-white p-3 rounded-xl shadow-sm border h-[48%] flex flex-col">
                        <div className="flex justify-between items-center mb-1">
                            <h3 className="text-sm font-semibold">Notes</h3>
                            <span className="text-[10px] text-green-600">{saving ? "Saving..." : "Saved"}</span>
                        </div>

                        <textarea
                            className="w-full flex-grow p-2 border rounded-lg focus:ring-blue-400 text-sm resize-none"
                            value={note}
                            onChange={handleNoteChange}
                            placeholder="Write a note..."
                        />
                    </div>
                </div>

                {/* ---------- PRESCRIPTIONS PANEL (reduced width) ---------- */}
                <div className="w-[20%] bg-white rounded-xl shadow-sm border p-3 h-full overflow-y-auto">
                    <div className="flex justify-between">
                        <h3 className="text-lg font-semibold">Prescriptions</h3>
                        <button
                            onClick={() => setIsOpen(true)}
                            className="text-blue-600 hover:underline"
                        >
                            Add +
                        </button>
                    </div>
                    
                    <AllPrescriptions patientId={patientInfo?.id} />
                </div>

                {/* ---------- MEDICATION SUMMARY (fills remaining space) ---------- */}
                <div className="bg-white rounded-xl shadow-sm border p-4 flex-1 overflow-y-auto">
                    <h2 className="text-md font-semibold mb-3">Medication Summary</h2>

                    {medicationData?.medication_wise_complaince_summary?.length ? (
                        medicationData.medication_wise_complaince_summary.map((m) => {
                            const medInfo = medicationData.medication_info.find(
                                (info) => info.id === m.medication_id
                            );
                            return (
                                <MedicationSummary
                                    key={m.medication_id}
                                    medication={medInfo}
                                    summary={m.medication_summary}
                                />
                            );
                        })
                    ) : (
                        <p className="text-center text-gray-500 py-4">No medication data available</p>
                    )}
                </div>
            </div>

            {/* ================= BOTTOM SECTION (50vh) ================= */}
            {/* ================= BOTTOM SECTION (50vh) ================= */}
            <div className="flex gap-4 px-4 pt-4 min-h-0">

                {/* Left Column */}
                <div className="w-[18%] flex flex-col gap-4 min-h-0">

                    <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} title="Add Prescription">
                        <PrescriptionForm
                            doctorId={doctorId}
                            patientId={id}
                            onClose={() => setIsOpen(false)}
                            firstName={patientInfo?.first_name}
                            lastName={patientInfo?.last_name}
                        />
                    </Modal>

                    {/* Reports section scroll fix */}
                    <div className="bg-white rounded-xl shadow-sm border p-4 h-full overflow-y-auto">
                        <Reports patientID={id} />
                    </div>
                </div>

                {/* Right Column (Graphs) */}
                <div className="bg-white rounded-xl shadow-sm border p-4 flex-1 min-h-0">

                    <h2 className="text-md font-semibold mb-3">Health Data</h2>

                    <div className="flex gap-4 h-full min-h-0">

                        <div className="w-1/2 h-full overflow-hidden">
                            <HealthGraphs patientID={id} defaultValueProp="GLUCOSE" />
                        </div>

                        <div className="w-1/2 h-full overflow-hidden">
                            <HealthGraphs patientID={id} defaultValueProp="INSULIN" />
                        </div>

                    </div>
                </div>

            </div>

        </div>
    );

};

export default Patient;
